export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/"

/** Build an absolute API URL even when NEXT_PUBLIC_API_URL is a same-origin path like "/api/". */
export function resolveApiUrl(path: string): URL {
  const base =
    API_BASE_URL.startsWith("http://") || API_BASE_URL.startsWith("https://")
      ? API_BASE_URL
      : typeof window !== "undefined"
        ? new URL(API_BASE_URL, window.location.origin).toString()
        : new URL(API_BASE_URL, "http://localhost:5173").toString()

  return new URL(path.replace(/^\//, ""), base.endsWith("/") ? base : `${base}/`)
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    message: string,
    public responseBody?: unknown
  ) {
    super(message)
    this.name = "ApiRequestError"
  }
}

export interface ApiContext {
  token?: string
  bookId?: string
  organizationId?: string
  refreshToken?: () => Promise<string | null>
}

async function isInactivityLogout(response: Response) {
  try {
    const data = await response.clone().json()
    return data?.error === "session_expired_inactivity"
  } catch {
    return false
  }
}

async function retryOnceIfUnauthorized(
  response: Response,
  context: ApiContext,
  url: string,
  options: RequestInit,
  headers: Record<string, string>
) {
  if (response.status !== 401 || !context.refreshToken || (await isInactivityLogout(response))) return response

  let freshToken: string | undefined
  try {
    freshToken = (await context.refreshToken()) ?? undefined
  } catch {
    return response
  }

  if (freshToken && freshToken !== context.token) {
    return fetch(url, { ...options, headers: { ...headers, Authorization: `Bearer ${freshToken}` } })
  }

  return response
}

export async function apiContextFetch<T>(context: ApiContext, route: string, options: RequestInit = {}): Promise<T> {
  const apiBaseUrl =
    typeof window === "undefined" && process.env.NEXT_INTERNAL_API_URL
      ? `${process.env.NEXT_INTERNAL_API_URL.replace(/\/$/, "")}/`
      : API_BASE_URL
  const urlObj = new URL(
    `${apiBaseUrl}${route}`,
    typeof window !== "undefined" ? window.location.origin : "http://localhost:3000"
  )

  if (context.organizationId) urlObj.searchParams.set("organization_id", context.organizationId)

  if (context.bookId) {
    urlObj.searchParams.set("book_id", context.bookId)
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
    ...(context.token ? { Authorization: `Bearer ${context.token}` } : {}),
  }

  const isFormDataBody = typeof FormData !== "undefined" && options.body instanceof FormData

  // Only set Content-Type if not explicitly undefined or inferred from FormData (for multipart/form-data)
  const optionHeaders = options.headers as Record<string, string | undefined>
  if (isFormDataBody) {
    delete headers["Content-Type"]
  } else if (!options.headers || !("Content-Type" in optionHeaders)) {
    headers["Content-Type"] = "application/json"
  } else if (optionHeaders["Content-Type"] === undefined) {
    // Remove Content-Type if explicitly set to undefined
    delete headers["Content-Type"]
  }

  const url = urlObj.toString()
  let response = await fetch(url, {
    ...options,
    headers,
  })
  response = await retryOnceIfUnauthorized(response, context, url, options, headers)

  const text = await response.text()
  let data = null
  try {
    data = JSON.parse(text)
  } catch {
    data = null
  }

  if (!response.ok) {
    if (data && "error" in data) {
      throw new ApiRequestError(response.status, data.error, data)
    } else if (data && "errors" in data && data.errors.base) {
      throw new ApiRequestError(response.status, data.errors.base[0], data)
    } else if (data && "errors" in data) {
      const message = Array.isArray(data.errors) ? data.errors.join(". ") : data.errors
      throw new ApiRequestError(response.status, message, data)
    }
    throw new ApiRequestError(response.status, "An unexpected error occurred", data)
  }

  if (response.status === 204) {
    return {} as T
  }

  return data || ({} as T)
}

async function apiContextRawFetch(context: ApiContext, route: string, options: RequestInit = {}): Promise<Response> {
  const urlObj = new URL(
    `${API_BASE_URL}${route}`,
    typeof window !== "undefined" ? window.location.origin : "http://localhost:3000"
  )

  if (context.organizationId) urlObj.searchParams.set("organization_id", context.organizationId)

  if (context.bookId) {
    urlObj.searchParams.set("book_id", context.bookId)
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
    ...(context.token ? { Authorization: `Bearer ${context.token}` } : {}),
  }

  const url = urlObj.toString()
  let response = await fetch(url, {
    ...options,
    headers,
  })
  response = await retryOnceIfUnauthorized(response, context, url, options, headers)

  if (!response.ok) {
    let errorMessage = "Request failed"
    try {
      const errorData = await response.json()
      errorMessage = errorData.error || errorData.message || errorMessage
    } catch {}
    throw new ApiRequestError(response.status, errorMessage)
  }

  return response
}

export async function apiContextBlobFetch(
  context: ApiContext,
  route: string,
  options: RequestInit = {}
): Promise<Blob> {
  const response = await apiContextRawFetch(context, route, options)
  return response.blob()
}

export async function apiContextTextFetch(
  context: ApiContext,
  route: string,
  options: RequestInit = {}
): Promise<string> {
  const response = await apiContextRawFetch(context, route, options)
  return response.text()
}
